import { useNavigate, useSearchParams } from "react-router-dom";
import { FileText, XIcon } from "lucide-react";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import useSubmissions from "@/features/submissions/hooks/use-submissions";
import useFormOptions from "@/features/submissions/hooks/use-form-options";
import { SubmissionCard } from "@/features/submissions/ui/submission-card";
import { ContentLayout } from "@/components/common/content-layout";
import { SearchBar } from "@/components/common/search-bar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Submissions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const page = parseInt(searchParams.get("page") || "0", 10);
  const search = searchParams.get("search") || "";
  const department = searchParams.get("department") || "ALL";
  const formId = searchParams.get("form_id") || "ALL";

  const forms = useFormOptions();
  // Drop empties — Radix <SelectItem> throws on an empty-string value.
  const categories = [...new Set(forms.map((f) => f.department))]
    .filter(Boolean)
    .sort();
  // Title options scope to the selected category (a form belongs to one dept).
  const titleOptions = (
    department === "ALL"
      ? forms
      : forms.filter((f) => f.department === department)
  ).filter((f) => f.source_id);

  const { data, error, isFetching, isLoading } = useSubmissions(
    page,
    search.trim() || undefined,
    department !== "ALL" ? department : undefined,
    formId !== "ALL" ? formId : undefined,
  );

  const submissions = data?.submissions || [];
  const currentPage = data?.currentPage || 0;
  const lastPage = data ? data.lastPage + 1 : 0;

  const handlePageChange = (next: number) => {
    if (next >= 0 && next < lastPage) {
      const params = new URLSearchParams(searchParams);
      params.set("page", next.toString());
      navigate(`?${params.toString()}`, { replace: true });
    }
  };

  const handleCategoryChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value === "ALL") params.delete("department");
    else params.set("department", value);
    // Title is scoped to category — drop a now-irrelevant title selection.
    params.delete("form_id");
    params.set("page", "0");
    setSearchParams(params);
  };

  const handleTitleChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value === "ALL") params.delete("form_id");
    else params.set("form_id", value);
    params.set("page", "0");
    setSearchParams(params);
  };

  return (
    <ContentLayout
      header={
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={department} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={formId} onValueChange={handleTitleChange}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Title" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All titles</SelectItem>
              {titleOptions.map((f) => (
                <SelectItem key={f.source_id} value={f.source_id}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Search sits at the right edge, filters stay left. */}
          <div className="ml-auto flex-1 min-w-[280px] max-w-[420px]">
            <SearchBar
              placeholder="Search name, email or phone…"
              urlParamStrategy="direct"
              clearButton={{
                enabled: true,
                position: "inside",
                ariaLabel: "Clear search",
                icon: <XIcon className="w-4 h-4" />,
              }}
            />
          </div>
        </div>
      }
      data={submissions}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load submissions"
      emptyState={{
        message: "No submissions found",
        icon: <FileText className="h-8 w-8 text-muted-foreground" />,
      }}
    >
      <SubmissionCard submissions={submissions} />
      {submissions.length > 0 && (
        <CustomPaginationComponent
          currentPage={currentPage}
          lastPage={lastPage}
          onPageChange={handlePageChange}
        />
      )}
    </ContentLayout>
  );
}
